import { Middleware } from 'redux';
import logger from 'redux-logger';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';

import { serialize, deserialize } from '@front/common/src/utils/serializer';
import { reducer as abFeatures } from '@front/common/src/ducks/common/ab-features';

import Config from '../utils/config';

const middlewares: Middleware[] = [];

const appReducer = combineReducers({
  abFeatures,
});

const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: ['abFeatures'],
  transforms: [
    createTransform(
      // transform state on its way to being serialized and persisted.
      (inboundState) => serialize(inboundState),
      // transform state being rehydrated
      (outboundState) => deserialize(outboundState),
      // define which reducers this transform gets called for.
      { blacklist: [] },
    ),
  ],
};

if (process.env.NODE_ENV === 'development' && Config.ENABLE_REDUX_LOGGER === 'true') {
  middlewares.push(logger);
}

export type RootState = ReturnType<typeof appReducer>;

const persistedReducer = persistReducer<RootState>(persistConfig, appReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
  stateTransformer: (state) => ({ ...state, universes: null, config: null }),
});

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable default serializability check middleware
      immutableCheck: false, // Disable default immutability check middleware
    }).concat(middlewares),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers.concat(sentryReduxEnhancer),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);
